import React, { useRef, useState, useEffect } from "react";

const NOTES = [
  { name: "A", file: "A.mp3" },
  { name: "A#", file: "ASharp.mp3" },
  { name: "B", file: "B.mp3" },
  { name: "C", file: "C.mp3" },
  { name: "C#", file: "CSharp.mp3" },
  { name: "D", file: "D.mp3" },
  { name: "D#", file: "DSharp.mp3" },
  { name: "E", file: "E.mp3" },
  { name: "F", file: "F.mp3" },
  { name: "F#", file: "FSharp.mp3" },
  { name: "G", file: "G.mp3" },
  { name: "G#", file: "GSharp.mp3" },
];

const TAALS = [
  { name: "Taal 1", file: "Taal1.mp3" },
  { name: "Taal 2", file: "Taal2.mp3" },
  { name: "Taal 3", file: "Taal3.mp3" },
  { name: "Taal 4", file: "Taal4.mp3" },
  { name: "Taal 5", file: "Taal5.mp3" },
  { name: "Taal 6", file: "Taal6.mp3" },
];

const SwaraPad = ({ tampuraVolume, tablaVolume, muted }) => {
  const audioRefs = useRef({});
  const [activeNotes, setActiveNotes] = useState({});
  const [activeTaals, setActiveTaals] = useState({});
  const [isMetronomeOn, setIsMetronomeOn] = useState(false);
  const [beat, setBeat] = useState(false);
  const [bpm, setBpm] = useState(100);
  const [metronomeVolume, setMetronomeVolume] = useState(0.5);

  const tickSound = useRef(null);

  useEffect(() => {
    tickSound.current = new Audio("/sounds/metronome-tick.mp3");
  }, []);

  const toggleLoop = (source, stateSetter, currentState, volumeLevel) => {
    const isActive = !!currentState[source.name];

    if (isActive) {
      audioRefs.current[source.name]?.pause();
      audioRefs.current[source.name].currentTime = 0;
      stateSetter((prev) => {
        const next = { ...prev };
        delete next[source.name];
        return next;
      });
    } else {
      if (!audioRefs.current[source.name]) {
        const audio = new Audio(`/sounds/${source.file}`);
        audio.loop = true;
        audio.volume = muted ? 0 : volumeLevel;
        audioRefs.current[source.name] = audio;
      } else {
        audioRefs.current[source.name].volume = muted ? 0 : volumeLevel;
      }
      audioRefs.current[source.name].play();
      stateSetter((prev) => ({ ...prev, [source.name]: true }));
    }
  };

  const toggleNote = (note) => toggleLoop(note, setActiveNotes, activeNotes, tampuraVolume);
  const toggleTaal = (taal) => toggleLoop(taal, setActiveTaals, activeTaals, tablaVolume);

  useEffect(() => {
    Object.entries(activeNotes).forEach(([name]) => {
      if (audioRefs.current[name]) {
        audioRefs.current[name].volume = muted ? 0 : tampuraVolume;
      }
    });
    Object.entries(activeTaals).forEach(([name]) => {
      if (audioRefs.current[name]) {
        audioRefs.current[name].volume = muted ? 0 : tablaVolume;
      }
    });
  }, [tampuraVolume, tablaVolume, muted, activeNotes, activeTaals]);

  useEffect(() => {
    let interval;
    if (isMetronomeOn) {
      const ms = (60 / bpm) * 1000;
      interval = setInterval(() => {
        setBeat((prev) => !prev);
        if (tickSound.current && !muted) {
          tickSound.current.volume = metronomeVolume;
          tickSound.current.currentTime = 0;
          tickSound.current.play();
        }
      }, ms);
    } else {
      setBeat(false);
    }
    return () => clearInterval(interval);
  }, [isMetronomeOn, bpm, muted, metronomeVolume]);

  return (
    <div className="flex flex-col items-center mt-2 space-y-6">
      <div className="flex flex-wrap justify-center gap-2">
        {NOTES.map((note) => (
          <button
            key={note.name}
            onClick={() => toggleNote(note)}
            className={`px-3 py-1 text-sm rounded shadow transition duration-200 ${
              activeNotes[note.name]
                ? "bg-green-300 text-black"
                : "bg-blue-100 hover:bg-blue-300"
            }`}
          >
            {note.name}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap justify-center gap-2 mt-2">
        {TAALS.map((taal) => (
          <button
            key={taal.name}
            onClick={() => toggleTaal(taal)}
            className={`px-3 py-1 text-sm rounded shadow transition duration-200 ${
              activeTaals[taal.name]
                ? "bg-yellow-300 text-black"
                : "bg-orange-100 hover:bg-orange-300"
            }`}
          >
            {taal.name}
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-col items-center">
        <button
          onClick={() => setIsMetronomeOn(!isMetronomeOn)}
          className="text-xs px-3 py-1 border border-gray-400 rounded hover:bg-gray-200"
        >
          {isMetronomeOn ? "Stop Metronome" : "Start Metronome"}
        </button>

        <label className="text-xs mt-2">BPM: {bpm}</label>
        <input
          type="range"
          min="30"
          max="230"
          value={bpm}
          onChange={(e) => setBpm(parseInt(e.target.value))}
          className="w-40 accent-red-500"
          list="bpm-marks"
        />
        <datalist id="bpm-marks">
          <option value="60" label="Slow" />
          <option value="90" label="Medium" />
          <option value="140" label="Fast" />
          <option value="200" label="Very Fast" />
        </datalist>

        <label className="text-xs mt-3">Metronome Volume</label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={metronomeVolume}
          onChange={(e) => setMetronomeVolume(parseFloat(e.target.value))}
          className="w-40 accent-blue-500"
          list="metronome-volume-ticks"
        />
        <datalist id="metronome-volume-ticks">
          {[...Array(11)].map((_, i) => (
            <option key={i} value={(i * 0.1).toFixed(1)} />
          ))}
        </datalist>

        <div className="relative mt-4 h-24 w-4">
          <div
            className={`absolute left-1/2 bottom-0 w-1 h-20 bg-red-500 origin-top transition-transform duration-200 ease-in-out ${
              isMetronomeOn ? (beat ? "-rotate-45" : "rotate-45") : "rotate-0"
            }`}
            style={{ transformOrigin: "top center" }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default SwaraPad;
